import { useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, ListMusic, ListPlus, Flag, X, DownloadCloud } from "lucide-react";
import { usePlaylistDialog } from "@/components/Collections";
import { toast } from "sonner";
import { usePlayer, cacheQueueOffline } from "@/store/player";
import { Waveform } from "@/components/Waveform";
import { ArtistBadge } from "@/components/ArtistBadge";
import { DmcaDialog } from "@/components/DmcaDialog";
import { SocialToggle } from "@/components/TrackItems";
import { Slider } from "@/components/ui/slider";
import { mediaUrl } from "@/lib/api";
import { fmtTime } from "@/lib/hubs";
import { useT } from "@/lib/i18n";

const QueuePanel = ({ onClose }) => {
  const { queue, index, playAt } = usePlayer();
  const t = useT();
  const offline = async () => {
    try {
      const n = await cacheQueueOffline();
      toast.success(`${n} tracks cached for offline listening`);
    } catch (e) {
      toast.error("Offline caching is not supported in this browser");
    }
  };
  return (
    <div data-testid="queue-panel" className="fixed right-4 bottom-28 w-80 max-h-[60vh] overflow-auto glass rounded-2xl p-3 z-50 reveal">
      <div className="flex items-center justify-between px-2 pb-2">
        <span className="eyebrow">{t("queue")} · {queue.length}</span>
        <div className="flex gap-2">
          <button data-testid="queue-offline-button" onClick={offline} title="Cache queue offline" className="text-zinc-400 hover:text-white"><DownloadCloud size={16} /></button>
          <button data-testid="queue-close-button" onClick={onClose} className="text-zinc-400 hover:text-white"><X size={16} /></button>
        </div>
      </div>
      {queue.map((q, i) => (
        <button key={`${q.track_id}-${i}`} data-testid={`queue-item-${i}`} onClick={() => playAt(i)}
          className={`w-full flex items-center gap-3 p-2 rounded-lg text-left ${i === index ? "bg-white/10" : "hover:bg-white/5"}`}>
          <img src={mediaUrl(q.cover_url)} alt="" className="w-9 h-9 rounded object-cover bg-zinc-800" />
          <div className="min-w-0">
            <div className="text-sm truncate">{q.title}</div>
            <div className="text-xs text-zinc-500 truncate">{q.artist?.name}</div>
          </div>
        </button>
      ))}
    </div>
  );
};

export const PlayerBar = () => {
  const p = usePlayer();
  const cur = p.queue[p.index];
  const [queueOpen, setQueueOpen] = useState(false);
  const [dmca, setDmca] = useState(false);
  if (!cur) return null;
  const dur = p.duration || cur.duration || 0;
  return (
    <div data-testid="persistent-player" className="fixed bottom-0 inset-x-0 z-50 h-24 bg-zinc-950/90 backdrop-blur-2xl border-t border-white/10">
      <div className="h-full max-w-[1600px] mx-auto px-4 flex items-center gap-4">
        <div className="flex items-center gap-3 w-56 lg:w-72 min-w-0 shrink-0">
          <img src={mediaUrl(cur.cover_url)} alt="" className="w-14 h-14 rounded-lg object-cover bg-zinc-800" />
          <div className="min-w-0">
            <Link to={`/track/${cur.track_id}`} data-testid="player-track-title" className="block font-semibold truncate hover:underline">{cur.title}</Link>
            <Link to={`/artist/${cur.artist?.slug}`} className="flex items-center gap-1 text-sm text-zinc-400 truncate hover:text-white">{cur.artist?.name} <ArtistBadge badge={cur.artist?.badge} size={12} /></Link>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid="player-prev-button" onClick={p.prev} className="p-2 text-zinc-400 hover:text-white" aria-label="Previous"><SkipBack size={18} fill="currentColor" /></button>
          <button data-testid="persistent-player-play-button" onClick={p.toggle} className="w-11 h-11 rounded-full bg-white text-black flex items-center justify-center hover:scale-105 active:scale-95 transition-transform" aria-label="Play/Pause">
            {p.isPlaying ? <Pause size={18} fill="black" /> : <Play size={18} fill="black" className="ml-0.5" />}
          </button>
          <button data-testid="player-next-button" onClick={p.next} className="p-2 text-zinc-400 hover:text-white" aria-label="Next"><SkipForward size={18} fill="currentColor" /></button>
        </div>
        <div className="hidden sm:flex flex-1 items-center gap-3 min-w-0">
          <span className="font-mono text-xs text-zinc-400 w-10 text-right" data-testid="player-current-time">{fmtTime(p.currentTime)}</span>
          <div className="flex-1 min-w-0">
            <Waveform peaks={cur.peaks} bars={160} height={40} duration={dur} progress={dur ? p.currentTime / dur : 0}
              onSeek={(f) => p.seek(f * dur)} comments={cur.comments || []} testId="persistent-player-waveform" />
          </div>
          <span className="font-mono text-xs text-zinc-400 w-10" data-testid="player-duration">{fmtTime(dur)}</span>
        </div>
        <div className="flex items-center gap-4 ml-auto shrink-0">
          <span className="hidden md:flex gap-4">
            <SocialToggle key={`l-${cur.track_id}`} track={cur} kind="like" />
            <SocialToggle key={`r-${cur.track_id}`} track={cur} kind="repost" />
          </span>
          <button data-testid="player-add-to-playlist" onClick={() => usePlaylistDialog.getState().open(cur)} className="text-zinc-400 hover:text-white" aria-label="Add to playlist" title="Add to playlist"><ListPlus size={18} /></button>
          <button data-testid="player-dmca-button" onClick={() => setDmca(true)} className="hidden md:block text-zinc-500 hover:text-red-400" aria-label="Report copyright"><Flag size={15} /></button>
          <div className="hidden lg:flex items-center gap-2 w-32">
            <button data-testid="player-mute-button" onClick={p.toggleMute} className="text-zinc-400 hover:text-white">{p.muted ? <VolumeX size={18} /> : <Volume2 size={18} />}</button>
            <Slider data-testid="player-volume-slider" value={[p.muted ? 0 : p.volume * 100]} max={100} step={1} onValueChange={([v]) => p.setVolume(v / 100)} />
          </div>
          <button data-testid="player-queue-button" onClick={() => setQueueOpen((v) => !v)} className={`${queueOpen ? "text-[var(--hub)]" : "text-zinc-400"} hover:text-white`} aria-label="Queue"><ListMusic size={18} /></button>
        </div>
      </div>
      {queueOpen && <QueuePanel onClose={() => setQueueOpen(false)} />}
      <DmcaDialog open={dmca} onOpenChange={setDmca} track={cur} />
    </div>
  );
};
